/**
 * Theme custody.
 *
 * The Figma collection "CRF Tokens v3" has two modes, Light and Dark, so the app
 * has two themes and nothing else. The resolution order is:
 *   1. an explicit choice the user made before  (`localStorage['crf.theme']`)
 *   2. otherwise the OS preference             (`prefers-color-scheme`)
 *
 * The FIRST paint is handled by an inline script in `index.html`, not here: a
 * React effect runs after the first paint, so a dark-mode user would see a white
 * flash. This provider adopts whatever that script already put on `<html>` and
 * owns it from then on. The storage key is shared between the two and must stay
 * in sync — it is `crf.theme` in both places.
 *
 * A user who has never chosen keeps FOLLOWING the OS: the media-query listener
 * stays attached until an explicit choice is stored. Once they choose, the choice
 * wins and the OS is ignored, which is the behaviour people expect from a toggle.
 *
 * ===========================================================================
 * WEB TIER A · WT7 — THE THIRD OPTION IS THE ABSENCE OF THE OTHER TWO
 * ===========================================================================
 * `settings.html` draws the theme control as THREE option cards: Light, Dark,
 * and "Follow this computer". That third card is not a third stored value —
 * following the OS is exactly the state this file has always had when nothing
 * is stored, and inventing a `'system'` string in localStorage would give the
 * same behaviour two spellings and let them disagree.
 *
 * So `choice` is DERIVED: `stored ?? 'system'`. Choosing Light or Dark stores
 * it; choosing "Follow this computer" REMOVES the key, which re-attaches the
 * media-query listener through the effect that was already keyed on `stored`.
 * `theme` (the resolved mode) and `followsSystem` are unchanged, so
 * `ThemeToggle` — the shell's existing consumer — is untouched.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type Theme = 'light' | 'dark';

/** What the Appearance control offers: the two modes, or "follow this computer". */
export type ThemeChoice = Theme | 'system';

const STORAGE_KEY = 'crf.theme';

interface ThemeContextValue {
  theme: Theme;
  /** True when the theme is following the OS because nothing is stored. */
  followsSystem: boolean;
  /** The SELECTED option — `'system'` is the derived absence of a stored mode. */
  choice: ThemeChoice;
  setTheme: (theme: Theme) => void;
  /** Light/Dark store the mode; `'system'` clears it and re-follows the OS. */
  setChoice: (choice: ThemeChoice) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStored(): Theme | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

function systemTheme(): Theme {
  return typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function apply(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
}

export function ThemeProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [stored, setStored] = useState<Theme | null>(() => readStored());
  const [system, setSystem] = useState<Theme>(() => systemTheme());

  const theme: Theme = stored ?? system;

  useEffect(() => {
    apply(theme);
  }, [theme]);

  // Keep following the OS until the user makes a choice.
  useEffect(() => {
    if (stored !== null) return undefined;
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (event: MediaQueryListEvent): void => {
      setSystem(event.matches ? 'dark' : 'light');
    };
    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  }, [stored]);

  const setTheme = useCallback((next: Theme) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage blocked: the choice still applies for this session, it just will
      // not survive a reload. Better than refusing to switch.
    }
    setStored(next);
  }, []);

  const setChoice = useCallback(
    (next: ThemeChoice) => {
      if (next !== 'system') {
        setTheme(next);
        return;
      }
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Same policy as the write above: the choice still applies for this
        // session. Refusing to go back to following the OS because a browser
        // refused a delete would strand the reader on their last explicit mode.
      }
      // Re-read the OS NOW: the listener below only fires on CHANGES, so a
      // reader who chose Dark while their computer is Light would otherwise sit
      // on the stale `system` value until the OS itself moved.
      setSystem(systemTheme());
      setStored(null);
    },
    [setTheme],
  );

  const toggleTheme = useCallback(() => {
    setTheme((stored ?? system) === 'dark' ? 'light' : 'dark');
  }, [setTheme, stored, system]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      followsSystem: stored === null,
      choice: stored ?? 'system',
      setTheme,
      setChoice,
      toggleTheme,
    }),
    [theme, stored, setTheme, setChoice, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === null) {
    throw new Error('useTheme must be used inside <ThemeProvider>.');
  }
  return context;
}
